import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { Activity, CheckCircle2, AlertTriangle, XCircle, MinusCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getAiSystemsStatus } from "@/lib/api/ai-status.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/ai-status")({
  component: AiStatusPage,
});

const STATUS_META = {
  ok: { label: "Operational", Icon: CheckCircle2, cls: "text-emerald-600" },
  degraded: { label: "Degraded", Icon: AlertTriangle, cls: "text-amber-600" },
  down: { label: "Failing", Icon: XCircle, cls: "text-destructive" },
  not_configured: { label: "Not configured", Icon: MinusCircle, cls: "text-muted-foreground" },
} as const;

function AiStatusPage() {
  const run = useServerFn(getAiSystemsStatus);
  const mutation = useMutation({ mutationFn: () => run({ data: undefined }) });

  useEffect(() => {
    mutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = mutation.data;
  const groups = [...new Set((data?.checks ?? []).map((c) => c.group))];

  return (
    <div className="space-y-4 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-navy text-navy-foreground">
            <Activity className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl text-navy">AI systems status</h1>
            <p className="text-sm text-muted-foreground">
              Live probes of every AI and media provider powering the site —
              assistant, research, scripts, voices, video and connectors.
            </p>
          </div>
        </div>
        <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          <RefreshCw className={`mr-2 h-4 w-4 ${mutation.isPending ? "animate-spin" : ""}`} />
          {mutation.isPending ? "Testing…" : "Run tests"}
        </Button>
      </header>

      {mutation.isError && (
        <p className="text-sm text-destructive">
          {(mutation.error as Error)?.message ?? "Status check failed."}
        </p>
      )}

      {data && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="secondary">{data.summary.ok} operational</Badge>
          {data.summary.degraded > 0 && <Badge variant="outline">{data.summary.degraded} degraded</Badge>}
          {data.summary.down > 0 && <Badge variant="destructive">{data.summary.down} failing</Badge>}
          {data.summary.notConfigured > 0 && (
            <Badge variant="outline">{data.summary.notConfigured} not configured</Badge>
          )}
          <span className="self-center text-muted-foreground">
            Checked {new Date(data.checkedAt).toLocaleTimeString()}
          </span>
        </div>
      )}

      {groups.map((group) => (
        <Card key={group}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{group}</CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            {(data?.checks ?? [])
              .filter((c) => c.group === group)
              .map((c) => {
                const meta = STATUS_META[c.status];
                const Icon = meta.Icon;
                return (
                  <div key={c.id} className="flex items-start gap-3 py-3">
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${meta.cls}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{c.label}</p>
                      <p className="break-words text-xs text-muted-foreground">{c.detail}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className={`text-xs font-medium ${meta.cls}`}>{meta.label}</p>
                      {c.latencyMs != null && (
                        <p className="text-[11px] text-muted-foreground">{c.latencyMs} ms</p>
                      )}
                    </div>
                  </div>
                );
              })}
          </CardContent>
        </Card>
      ))}

      {!data && mutation.isPending && (
        <p className="text-sm text-muted-foreground">Running live probes against every provider…</p>
      )}
    </div>
  );
}
